<template>
  <div class="bg-white brand-input">
    <div v-if="!loadingBlueprint">
      <!-- Non-empty state -->
      <div>
        <div v-if="!isSubscribed && false" class="bg-brand-black text-center w-full text-white py-1 text-sm cursor-pointer" @click="openChat">
          Want to get started with a free trial? 🤝 Ask away️ ->
        </div>
        <header class="sticky top-0 z-2005 lg:flex lg:items-center lg:justify-between bg-brand-sand py-4 px-4">
          <div v-if="currentSegment" class="flex space-x-2 text-sm font-semibold pl-4 lg:w-96 justify-center lg:justify-start">
            <span class="text-brand-brown whitespace-nowrap">
              <router-link to="/cancellation/flows"> Cancel Flows </router-link>
            </span>
            <span class="text-brand-medium-brown"> / </span>
            <span class="text-brand-brown whitespace-nowrap" v-if="segmentInABTest">
              <router-link to="/cancellation/flows">
                {{ segmentInABTest.name }}
              </router-link>
              <span class="text-brand-medium-brown"> / </span>
            </span>
            <span class="text-brand-black overflow-ellipsis overflow-hidden whitespace-nowrap">
              {{ currentSegment.name || 'New Cancel Flow' }}
            </span>
          </div>
          <div v-else>
            <router-link
              class="text-center lg:text-left lg:flex-grow-0 justify-left lg:w-96 flex-grow-1 flex items-center pl-4 cursor-pointer"
              :to="{ path: segmentsEnabled ? '/cancellation/flows' : '/' }"
            >
              <span><font-awesome-icon :icon="['fas', 'arrow-left']" class="text-brand-brown"></font-awesome-icon></span>
              <img src="@/assets/brand/icon.svg" class="w-auto inline-block h-14 p-2.5 rounded-full" alt="Logo" />
            </router-link>
          </div>
          <div
            class="mx-auto flex flex-grow max-w-screen-sm text-center text-brand-brown font-semibold text-sm space-x-4 lg:space-x-8 justify-center mt-4 lg:mt-0"
          >
            <div v-for="(step, i) in steps" :key="step.value">
              <builder-tab :value="step.value" :label="step.name" :active="i === currentStepIndex" @select="(v) => handleStepClick(i, v)" />
            </div>
          </div>
          <!-- <steps :steps="buildSteps" :current="1" @step-click="handleStepClick" class="flex-grow" /> -->
          <div
            class="flex flex-col items-center flex-grow-0 mt-2 mx-auto lg:mx-3 lg:mt-0 lg:flex-shrink-0 lg:space-x-3 lg:flex-row lg:w-96 lg:mr-4 lg:justify-end"
          >
            <!-- Segment save state on audience tab -->
            <save-state-tag v-if="steps[currentStepIndex].value === 'audience'" :status="currentSegmentSaveState" class="py-2" />
            <!-- Blueprint save state -->
            <save-state-tag v-else :status="currentBlueprintSaveState" />
            <!-- hide if current segment doesn't have a published blueprint -->
            <div
              class="mx-auto my-2 lg:inline-block lg:my-0 space-x-3"
              v-if="!(steps[currentStepIndex].value === 'audience' && currentSegment && !currentSegment.activeBlueprint)"
            >
              <el-tooltip effect="dark" content="Preview Flow" placement="left">
                <el-dropdown split-button @click="previewFlow" command="previewFlow">
                  <!-- <el-button type="plain" size="small"> -->
                  <font-awesome-icon :icon="['fas', 'eye']" class=""></font-awesome-icon>
                  <!-- </el-button> -->
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item v-for="lang in orgLanguages" :key="lang.value" @click.native="previewFlow(lang.value)">
                      <span class="font-medium">{{ lang.label }}</span>
                    </el-dropdown-item>
                  </el-dropdown-menu>
                </el-dropdown>
                <!-- <button
                  id="preview-flow-trigger"
                  class="px-3 py-1 bg-white border-2 rounded-md border-brand-gray-blue hover:border-brand-blue cursor-pointer text-brand-blue transition-colors text-lg"
                  @click="previewFlow"
                >
                  <font-awesome-icon :icon="['fas', 'eye']" class=""></font-awesome-icon>
                </button> -->
              </el-tooltip>
              <!-- Publish blueprint button -->
              <el-button
                v-if="steps[currentStepIndex].value === 'audience'"
                type="primary"
                @click="handleSaveSegment"
                :loading="currentSegmentSaveState === SAVE_STATE.SAVING"
                :disabled="currentSegmentSaveState !== SAVE_STATE.UNSAVED"
                id="audience-save"
                ><span class="font-bold">Update Audience</span></el-button
              >
              <div
                v-else-if="(currentBlueprintMatchesActive || currentBlueprintMatchesSegmentActive) && currentBlueprintSaveState === SAVE_STATE.SAVED"
                class="bg-brand-medium-blue text-brand-blue inline-flex items-center px-4 py-2 text-sm leading-6 font-medium rounded-md transition ease-in-out duration-150 space-x-2"
              >
                <!-- <font-awesome-icon :icon="['fas', 'check-circle']" class="text-brand-brown" /> -->
                <span class="cursor-default font-semibold">Changes Pushed</span>
              </div>
              <el-button
                v-else
                v-can-enable="'cf:write'"
                type="primary"
                @click="handlePublish"
                :loading="publishLoading"
                :disabled="currentBlueprintSaveState !== SAVE_STATE.SAVED"
                id="flow-publish"
                ><span class="font-bold">{{
                  currentSegment && !currentSegment.activeBlueprint ? 'Activate Segment' : 'Push Changes'
                }}</span></el-button
              >
            </div>
          </div>
        </header>
        <main v-if="currentBlueprint" v-loading="publishLoading || currentBlueprintLoadState === LOAD_STATE.LOADING">
          <div class="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
            <section>
              <div class="container max-w-screen-xl mt-8 rounded-lg">
                <flow-builder v-if="steps[currentStepIndex].value === 'builder'" @next="nextStep" />
                <offer-builder v-if="steps[currentStepIndex].value === 'offers'" @next="nextStep" @back="backStep" />
                <design v-if="steps[currentStepIndex].value === 'design'" @next="nextStep" />
                <segment-builder v-if="steps[currentStepIndex].value === 'audience'" @next="nextStep" />
                <embed-code v-if="steps[currentStepIndex].value === 'embed'" @next="nextStep" />
              </div>
            </section>
            <!-- UPGRADE MODAL -->
            <el-dialog :visible.sync="showUpgradePrompt" custom-class="mx-auto max-w-xl rounded-lg" :close-on-click-modal="true">
              <div class="has-border-radius-16">
                <div class="max-w-lg mx-auto my-2 text-brand-brown font-semibold">
                  <h3 class="text-xl leading-6 my-6 font-bold text-gray-900">Ready to go live with Cancel Flows?</h3>
                  <p class="break-normal text-md font-semibold">
                    Great news. Now that you've got a cancel flow built and you’re ready to push it to your live product, it's time for Churnkey to
                    gather your payment details. Once that's completed, you'll start your 14-day trial.
                  </p>
                </div>
                <div slot="footer" class="dialog-footer mt-8 border-t-2 border-brand-sand bg-brand-light-sand">
                  <div class="max-w-md mx-auto py-5 flex justify-center space-x-4 items-center">
                    <el-button @click="showUpgradePrompt = false" id="publish-payment-cancel">Not Now</el-button>
                    <a href="/upgrade">
                      <el-button type="primary">View Our Plans</el-button>
                    </a>
                  </div>
                </div>
              </div>
            </el-dialog>
          </div>
        </main>
      </div>
    </div>
    <div v-else>
      <div class="bg-brand-sand w-full h-20"></div>
      <div class="container max-w-screen-md pt-20 space-y-12" element-loading-background="rgba(255, 255, 255, 0.05)">
        <content-loader primaryColor="#f8efe6" secondaryColor="#f8efe655" class="w-full" :speed="1.5" :primaryOpacity="0.9" :secondaryOpacity="0.9" />
        <content-loader
          primaryColor="#f8efe6"
          secondaryColor="#f8efe655"
          class="w-full"
          :speed="1.5"
          :primaryOpacity="0.65"
          :secondaryOpacity="0.65"
        />
        <content-loader primaryColor="#f8efe6" secondaryColor="#f8efe655" class="w-full" :speed="2.5" :primaryOpacity="0.5" :secondaryOpacity="0.5" />
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import * as mutations from '@/store/mutation-types';
import * as actions from '@/store/action-types';
import { SAVE_STATE, LOAD_STATE } from '@/store/shared';
import FlowBuilder from '@/components/Builder/FlowBuilder.vue';
import OfferBuilder from '@/components/Builder/OfferBuilder.vue';
import EmbedCode from '@/components/Cancellation/CancellationEmbedCode.vue';
import Design from '@/components/Cancellation/CancellationDesign.vue';
import SegmentBuilder from '@/components/Builder/SegmentBuilder.vue';
import BuilderTab from '@/components/UI/BuilderTab.vue';
import SaveStateTag from '@/components/UI/SaveStateTag.vue';
import { ContentLoader } from 'vue-content-loader';
import eventBus from '@/helpers/event-bus';

const baseBuildSteps = [
  { name: 'Cancel Flow', value: 'builder' },
  { name: 'Offers', value: 'offers' },
  // { name: 'Design', value: 'design' },
  // { name: 'Embed', value: 'embed' },
];

const segmentedBuildSteps = [
  { name: 'Audience', value: 'audience' },
  { name: 'Cancel Flow', value: 'builder' },
  { name: 'Offers', value: 'offers' },
];

export default {
  name: 'Builder',
  components: {
    // Steps,
    BuilderTab,
    EmbedCode,
    OfferBuilder,
    SegmentBuilder,
    FlowBuilder,
    SaveStateTag,
    ContentLoader,
    Design,
  },
  data() {
    return {
      SAVE_STATE,
      LOAD_STATE,
      publishLoading: false,
      currentStepIndex: this.$route.query.step || 0,
      loadingPortal: false,
      showUpgradePrompt: false,
      loadingBlueprint: true,
    };
  },
  async created() {
    await this.$store.dispatch(actions.SYNC_USER_RECORD);
    this.$store.commit(mutations.SET_SELECTED_TEMPLATE, '');
    this.$store.commit(mutations.SET_CURRENT_BLUEPRINT_ID, this.$route.params.id);
    this.loadingBlueprint = false;
  },
  computed: {
    ...mapGetters([
      'currentBlueprintMatchesActive',
      'currentBlueprintMatchesSegmentActive',
      'currentBlueprintSaveState',
      'currentBlueprint',
      'currentBlueprintLoadState',
      'currentBlueprintId',
      'currentSegment',
      'currentSegmentSaveState',
      'segmentsEnabled',
      'abTests',
      'orgLanguages',
      'isLocal',
      'isAdmin',
    ]),
    ...mapGetters({
      subscription: 'userOrgSubscription',
    }),
    isSubscribed() {
      return this.subscription && this.subscription.plan && this.subscription.plan.name !== 'Free';
    },
    steps() {
      if (this.currentSegment) {
        return segmentedBuildSteps;
      }
      return baseBuildSteps;
    },
    segmentInABTest() {
      if (this.currentSegment && this.abTests) {
        const segmentId = this.currentSegment._id;
        return this.abTests.find((item) => item.segments.includes(segmentId) && !item.completedAt);
      }
      return false;
    },
  },
  methods: {
    openChat() {
      eventBus.$emit('openChat');
    },
    previewFlow(language) {
      this.$store.commit(mutations.SET_PREVIEW_INLINE, false);
      this.$store.dispatch(actions.LAUNCH_EMBED_PREVIEW, { language });
    },
    handleStepClick(stepIndex) {
      this.currentStepIndex = stepIndex;
    },
    nextStep() {
      this.handleStepClick(this.currentStepIndex + 1);
    },
    backStep() {
      this.handleStepClick(this.currentStepIndex - 1);
    },
    handleSaveSegment() {
      if (this.currentSegment) {
        this.$store.dispatch(actions.SEGMENT_SCHEDULE_SAVE, { id: this.currentSegment._id, delay: 0 });
      }
    },
    async handlePublish() {
      if (
        !this.isLocal &&
        // This could be replaced with !this.isSubscribed, unless there are cases
        // where this.subscription is true while this.subscription.plan is falsey
        (!this.subscription || (this.subscription.plan && this.subscription.plan.name === 'Free')) &&
        !this.isAdmin
      ) {
        this.showUpgradePrompt = true;
      } else {
        this.publishLoading = true;
        if (this.currentSegment) {
          await this.$store.dispatch(actions.BLUEPRINT_SEGMENT_PUBLISH, { id: this.currentBlueprint._id, segmentId: this.currentSegment._id });
        } else {
          await this.$store.dispatch(actions.BLUEPRINT_PUBLISH, { id: this.currentBlueprint._id });
        }
        this.publishLoading = false;
      }
    },
  },
};
</script>
<style lang="scss">
.el-dialog {
  border-radius: 10px;
  .el-dialog__header {
    padding-top: 0;
  }
  .el-dialog__body {
    padding: 0;
  }
  .dialog-footer {
    border-radius: 0 0 10px 10px;
  }
  .el-dialog__headerbtn {
    display: none;
  }
  /* Fix Bouncing Loader Button */
  .el-button--primary:focus {
    outline: none;
  }
  .el-loading-mask:focus {
    outline: none;
  }
  .el-loading-spinner:focus {
    outline: none;
  }
  .el-loading-spinner .circular:focus {
    outline: none;
  }
}
</style>

<style lang="scss" scoped>
.pulse {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #6d6156;
  cursor: pointer;
  box-shadow: 0 0 0 #6d615677;
  animation: pulse 1.5s infinite;
}

@-webkit-keyframes pulse {
  0% {
    -webkit-box-shadow: 0 0 0 0 #6d615677;
  }
  70% {
    -webkit-box-shadow: 0 0 0 10px rgba(204, 169, 44, 0);
  }
  100% {
    -webkit-box-shadow: 0 0 0 0 rgba(204, 169, 44, 0);
  }
}
@keyframes pulse {
  0% {
    -moz-box-shadow: 0 0 0 0 #6d615677;
    box-shadow: 0 0 0 0 #6d615677;
  }
  70% {
    -moz-box-shadow: 0 0 0 10px rgba(204, 169, 44, 0);
    box-shadow: 0 0 0 10px rgba(204, 169, 44, 0);
  }
  100% {
    -moz-box-shadow: 0 0 0 0 rgba(204, 169, 44, 0);
    box-shadow: 0 0 0 0 rgba(204, 169, 44, 0);
  }
}
</style>
